import subpixel from '../util/canvasSubpixelOffset';

/** Interactive handle */
export default class Handle {
	constructor(tool, params) {
		this.tool = tool;
		this.core = tool.core;
		this.state = {
			visible: true,
			hover: false
		};
		this.params = {
			date: new Date(params.date).getTime(),
			value: Number(params.value),
			pairedHandle: params.pairedHandle,
			pairedOffsetX: params.pairedOffsetX,
			pairedOffsetY: params.pairedOffsetY,
			style: this.core.getStyle('tool.handle')
		};
	}

	/**
	 * x calc - go from date to idx to px
	 * y calc - go from value to px
	 */
	getCoords() {
		const { panel } = this.tool;
		const isPaired = typeof this.params.pairedHandle === 'number';
		const handleCoords = isPaired
			? this.tool.handle[this.params.pairedHandle]
			: this;
		const xIdx = this.core.closestExchangeIndex(
			new Date(handleCoords.params.date)
		);
		let xPx = panel.xAxis.primaryScale(xIdx);
		let yPx = 0;

		if (panel.isNormalized) {
			// render as normalized value.  value stays as an actual.
			yPx = panel.yAxis.primaryScale(
				this.tool.panel.actualToNormalized(handleCoords.params.value)
			);
		} else {
			yPx = panel.yAxis.primaryScale(handleCoords.params.value);
		}

		// check if handle is earlier than ruler (can happen when drawing handles in inter and switching to intra)
		if (
			new Date(handleCoords.params.date) -
				new Date(this.core.exchangeDates[0]) <
				0 &&
			this.tool.params.numberOfAnchors > 1
		) {
			return null;
		}

		if (isPaired) {
			xPx += this.params.pairedOffsetX;
			yPx += this.params.pairedOffsetY;
		}

		return [xPx, yPx];
	}

	isWithin(mousePanel) {
		const coords = this.getCoords();
		const tolerance = this.core.state.isTouchDevice ? 5 : 3; // use a larger hitbox for touch events
		const size = this.params.style.length * tolerance;
		const half = size / 2;
		const top = this.tool.panel.size.padding.top;

		if (!coords) {
			return false;
		}

		return (
			mousePanel[0] >= coords[0] - half &&
			mousePanel[1] - top >= coords[1] - half &&
			mousePanel[0] < coords[0] - half + size &&
			mousePanel[1] - top < coords[1] - half + size
		);
	}

	onClick() {
		// onClick fired
	}

	render() {
		const ctx = this.tool.panel.rootToolContext;
		if (this.state.visible) {
			const coords = this.getCoords();
			if (!coords) return;

			if (this.renderCustom) {
				return this.renderCustom(ctx, coords, this.params);
			}

			const lengthDefault = this.params.style.length;
			const lengthHover = this.params.style.hover.length;
			let length = this.state.hover ? lengthHover : lengthDefault;

			ctx.beginPath();
			ctx.lineWidth = this.params.style.width;

			length /= 4;

			ctx.arc(
				subpixel.px(coords[0] - length / 2),
				subpixel.px(coords[1] - length / 2),
				length * 2,
				0,
				2 * Math.PI
			);

			if (this.tool.state.selected) {
				ctx.strokeStyle = this.params.style.color;
				ctx.fillStyle = this.params.style.background.color;
				ctx.lineWidth = this.params.style.width;
				ctx.fill();
				ctx.stroke();
			} else if (this.state.hover) {
				ctx.strokeStyle = this.params.style.hover.color;
				ctx.fillStyle = this.params.style.hover.background.color;
				ctx.lineWidth = this.params.style.hover.width;
				ctx.fill();
				ctx.stroke();
			}
		}
	}

	/** Gets generic object representation of Handle */
	toJSON() {
		let value = null;
		if (typeof this.params.pairedHandle === 'number') {
			// some tools (text) use a paired handle
			value = {
				pairedHandle: this.params.pairedHandle,
				pairedOffsetX: this.params.pairedOffsetX,
				pairedOffsetY: this.params.pairedOffsetY
			};
		} else {
			// default tool handle
			value = {
				date: this.params.date,
				value: this.params.value
			};
		}
		return value;
	}
	/**
	 * Gets instance of Handle based on generic JSON object provided
	 * @method toJSON
	 * @params {Object} json
	 * @params {Tool} tool
	 */
	static fromJSON(json, tool) {
		return new Handle(tool, {
			date: json.date,
			value: json.value,
			pairedHandle: json.pairedHandle,
			pairedOffsetX: json.pairedOffsetX,
			pairedOffsetY: json.pairedOffsetY
		});
	}
}
